/**
 * The Admin module's payload shapes.
 *
 * Beside the pages rather than in lib/types.ts because each of these is read by
 * exactly one page — the split the NOC module's nocTypes.ts explains. Only
 * `AdminSummary`, which the dashboard tile needs, lives in the shared file.
 *
 * Names mirror the JSON the API sends, which is camelCase throughout this module
 * even though the rest of the app passes SQL column names through untouched.
 * That is deliberate and worth knowing: these payloads are COMPUTED — a weighted
 * service level is not a column — so there was no column name to preserve, and
 * inventing PascalCase for a derived figure would imply one.
 */

import type { Band } from '../../lib/format';

/* ── Shared vocabularies ────────────────────────────────────────────────── */

export type KpiStatus = 'on_target' | 'at_risk' | 'off_target' | 'no_data';
export type Severity = 'critical' | 'serious' | 'warning';

/** Which tab an alert or a drill-in belongs to. */
export type AdminTab =
  | 'overview'
  | 'scorecard'
  | 'calls'
  | 'procurement'
  | 'salesOrders'
  | 'contracts'
  | 'depot'
  | 'vendors'
  | 'sops'
  | 'registrations'
  | 'people';

/* ── Overview ───────────────────────────────────────────────────────────── */

export interface AdminAlert {
  severity: Severity;
  module: string;
  message: string;
  tab: string;
}

export interface AdminOverviewData {
  alerts: AdminAlert[];
  /**
   * How many rows each register holds.
   *
   * The page needs this to tell "nothing needs attention" apart from "nothing
   * has been entered", which look identical from an alert list alone and mean
   * opposite things.
   */
  registers: {
    contracts: number;
    purchaseOrders: number;
    assets: number;
    sops: number;
    registrations: number;
    vendors: number;
    callRows: number;
    kpis: number;
  };
  calls: {
    offered30d: number;
    serviceLevelPercent: number | null;
    serviceLevelPreviousPercent: number | null;
    abandonRatePercent: number | null;
    abandonRatePreviousPercent: number | null;
    avgSpeedAnswerSec: number | null;
    firstCallResPercent: number | null;
    csatScore: number | null;
  };
  procurement: {
    openPoCount: number;
    openPoValue: number;
    overdueReceipts: number;
    spend60d: number;
  };
  contracts: {
    active: number;
    clientMrr: number;
    expiring90: number;
    valueAtRisk90: number;
    noticeDeadline30: number;
  };
  depot: { inStockUnits: number; stockValue: number; rmaOpen: number; warrantyExpiring90: number };
  sops: { active: number; overdueReview: number; dueIn30: number };
  vendors: { total: number; strategic: number; annualSpend: number; reviewsOverdue: number };
  registrations: { total: number; lapsed: number; expiring30: number };
  people: { headcount: number };
  scorecard: {
    period: string;
    total: number;
    onTarget: number;
    atRisk: number;
    offTarget: number;
    noData: number;
    weightedHealth: number | null;
  };
}

/* ── Calls ──────────────────────────────────────────────────────────────── */

export interface CallWindow {
  offered: number;
  answered: number;
  abandoned: number;
  voicemails: number;
  afterHours: number;
  longestWaitSec: number | null;
  serviceLevelPercent: number | null;
  answerRatePercent: number | null;
  abandonRatePercent: number | null;
  avgSpeedAnswerSec: number | null;
  avgHandleTimeSec: number | null;
  firstCallResPercent: number | null;
  csatScore: number | null;
}

export interface CallsData {
  window: number;
  queue: string | null;
  current: CallWindow;
  /** The same-length window immediately before, so a delta is like-for-like. */
  previous: CallWindow;
  daily: {
    metricDate: string;
    callsOffered: number;
    callsAnswered: number;
    callsAbandoned: number;
    afterHoursCalls: number;
    serviceLevelPercent: number | null;
    avgSpeedAnswerSec: number | null;
    avgHandleTimeSec: number | null;
    firstCallResPercent: number | null;
  }[];
  byQueue: {
    queue: string;
    callsOffered: number;
    callsAnswered: number;
    callsAbandoned: number;
    serviceLevelPercent: number | null;
    avgSpeedAnswerSec: number | null;
  }[];
  /** 0 = Sunday — see the weekday note in routes/adminAnalytics.ts. */
  byWeekday: { weekday: number; avgOffered: number | null; avgAbandoned: number | null }[];
  /** What data exists at all, ignoring the window. */
  coverage: { rows: number; firstDate: string | null; lastDate: string | null };
}

/* ── Procurement ────────────────────────────────────────────────────────── */

export interface ProcurementData {
  window: number;
  summary: {
    poCount: number;
    costTotal: number;
    sellTotal: number;
    grossProfit: number;
    grossMarginPercent: number | null;
    received: number;
    /** Null until something has been received — 100% accuracy over zero
     *  receipts is a claim nobody has earned. */
    receivingAccuracyPercent: number | null;
    shortShipRatePercent: number | null;
    depotStockingPercent: number | null;
    onTimeDeliveryPercent: number | null;
    /** Signed: negative means arriving before the promise. */
    avgSlipDays: number | null;
    avgOrderToReceiptDays: number | null;
    avgQuoteTurnaroundDays: number | null;
    openPoCount: number;
    openPoValue: number;
    overdueReceiptCount: number;
  };
  monthly: {
    period: string;
    poCount: number;
    costTotal: number;
    sellTotal: number;
    grossProfit: number;
  }[];
  byCategory: { category: string; poCount: number; costTotal: number }[];
  byStatus: { status: string; poCount: number; costTotal: number }[];
  exceptions: { reason: string; count: number }[];
  topVendors: { vendorName: string; poCount: number; costTotal: number }[];
  openOrders: {
    purchaseOrderId: number;
    poNumber: string;
    description: string;
    status: string;
    orderDate: string;
    promisedDate: string | null;
    costTotal: number;
    vendorName: string | null;
    clientName: string | null;
    daysOverdue: number | null;
  }[];
}

/* ── Contracts ──────────────────────────────────────────────────────────── */

export interface ContractsData {
  summary: {
    total: number;
    active: number;
    clientMrr: number;
    clientArr: number;
    vendorAnnualCost: number;
    expiring90: number;
    valueAtRisk90: number;
    noticeDeadline30: number;
    /** Non-renewing, past the notice date, term still running. The expensive
     *  case: a decision that defaulted without anybody making it. */
    noticeMissed: number;
    expired: number;
    autoRenewSharePercent: number | null;
  };
  buckets: { key: string; label: string; count: number; annualValue: number }[];
  byType: { contractType: string; count: number; annualValue: number }[];
  timeline: { period: string; count: number; annualValue: number }[];
  upcoming: {
    contractId: number;
    contractName: string;
    counterpartyName: string;
    clientName: string | null;
    partyType: string;
    contractType: string;
    endDate: string;
    noticeDeadline: string;
    daysToEnd: number;
    daysToNotice: number;
    noticeDays: number;
    autoRenew: boolean;
    status: string;
    monthlyValue: number;
    annualValue: number;
    ownerName: string | null;
  }[];
}

/* ── Depot ──────────────────────────────────────────────────────────────── */

export interface DepotData {
  summary: {
    lineItems: number;
    totalUnits: number;
    inStockUnits: number;
    stockValue: number;
    totalValue: number;
    deployed: number;
    rmaOpen: number;
    awaitingDisposal: number;
    faulty: number;
    warrantyExpiring90: number;
    outOfWarranty: number;
    auditOverdue90: number;
    unassignedReserved: number;
  };
  byStatus: { status: string; lineItems: number; units: number; value: number }[];
  byCategory: { category: string; lineItems: number; units: number; value: number }[];
  byCondition: { condition: string; units: number }[];
  byLocation: { depotLocation: string; units: number; value: number }[];
  attention: {
    depotAssetId: number;
    assetTag: string;
    category: string;
    manufacturer: string | null;
    model: string | null;
    condition: string;
    status: string;
    depotLocation: string | null;
    bin: string | null;
    quantity: number;
    lineValue: number;
    warrantyExpiry: string | null;
    daysToWarrantyExpiry: number | null;
    assignedClientName: string | null;
  }[];
}

/* ── SOPs ───────────────────────────────────────────────────────────────── */

export interface SopsData {
  summary: {
    total: number;
    live: number;
    active: number;
    draft: number;
    underReview: number;
    retired: number;
    overdueReview: number;
    dueIn30: number;
    critical: number;
    neverReviewed: number;
    /** Live SOPs with no next-review date. Neither current nor overdue —
     *  unmanaged, which is its own problem. */
    noReviewDate: number;
    linkedToKpi: number;
    currencyPercent: number | null;
  };
  byCategory: { category: string; count: number; overdue: number }[];
  byOwner: { ownerName: string; count: number; overdue: number }[];
  byCriticality: { criticality: string; count: number }[];
  dueSoon: {
    sopRegisterId: number;
    sopCode: string;
    title: string;
    category: string;
    criticality: string;
    status: string;
    version: string;
    lastReviewed: string | null;
    nextReview: string;
    daysToReview: number;
    ownerName: string | null;
    linkedKpiName: string | null;
  }[];
}

/* ── Vendors ────────────────────────────────────────────────────────────── */

export interface VendorsData {
  summary: {
    total: number;
    active: number;
    strategic: number;
    watchlist: number;
    annualSpend: number;
    avgRelationshipScore: number | null;
    avgOnTimeShipPercent: number | null;
    unscored: number;
    reviewsOverdue: number;
    reviewsDue60: number;
    noReviewScheduled: number;
    rebateEligible: number;
    /** Top three vendors' share of total spend — how exposed the business is to
     *  one supplier changing terms, which a total spend figure hides. */
    spendConcentrationPercent: number | null;
  };
  byTier: { tier: string; count: number; annualSpend: number }[];
  byCategory: { category: string; count: number; annualSpend: number }[];
  topSpend: {
    adminVendorId: number;
    name: string;
    tier: string;
    category: string;
    annualSpend: number;
    avgMarginPercent: number | null;
    onTimeShipPercent: number | null;
    relationshipScore: number | null;
    status: string;
  }[];
  reviewsDue: {
    adminVendorId: number;
    name: string;
    tier: string;
    annualSpend: number;
    relationshipScore: number | null;
    lastReviewDate: string | null;
    nextReviewDate: string;
    daysToReview: number;
    internalOwnerName: string | null;
  }[];
}

/* ── Registrations ──────────────────────────────────────────────────────── */

export interface RegistrationsData {
  summary: {
    total: number;
    notRequired: number;
    lapsed: number;
    expiring30: number;
    expiring90: number;
    inRenewal: number;
    /** Inside its own lead time, so it needs starting now — not the same as
     *  "expires in 30 days", because the lead time is per item. */
    actionRequired: number;
    annualPremium: number;
    totalCoverage: number;
    inDatePercent: number | null;
  };
  byType: { itemType: string; count: number; premiumAnnual: number; coverageAmount: number }[];
  timeline: {
    registrationId: number;
    name: string;
    itemType: string;
    issuer: string | null;
    jurisdiction: string | null;
    identifier: string | null;
    expiryDate: string;
    renewalLeadDays: number;
    status: string;
    coverageAmount: number | null;
    premiumAnnual: number | null;
    ownerName: string | null;
    daysToExpiry: number;
    actionRequired: boolean;
  }[];
}

/* ── People and comms ───────────────────────────────────────────────────── */

export interface PeopleBoardPerson {
  employeeId: number;
  fullName: string;
  email: string | null;
  roleTitle: string | null;
  departmentName: string | null;
  location: string | null;
  /** The next occurrence. For a birthday the API sends ONLY this, never the
   *  original date — the year is the person's business. */
  nextDate: string;
  daysAway: number;
}

export interface PeopleAnniversary extends PeopleBoardPerson {
  date: string;
  years: number;
  milestone: boolean;
  tenureYears: number | null;
}

export interface AdminPeopleData {
  horizon: number;
  summary: {
    headcount: number;
    onLeave: number;
    avgTenureYears: number | null;
    birthdays30: number;
    anniversaries30: number;
    milestones: number;
    /** How much the board does NOT know. Three names means something different
     *  if twenty-seven people have no date recorded. */
    birthDateUnknown: number;
    startDateUnknown: number;
    departments: number;
  };
  birthdays: PeopleBoardPerson[];
  anniversaries: PeopleAnniversary[];
  byDepartment: { departmentName: string; headcount: number }[];
  tenureBands: { key: string; label: string; headcount: number }[];
  announcements: AnnouncementFeedItem[];
}

/**
 * A noticeboard entry as the People tab reads it.
 *
 * Distinct from `AnnouncementRow` rather than shared, because it comes from a
 * different endpoint with a different shape and a different job: that one is the
 * register row you edit, this one is the read-only feed the board renders. One
 * type covering both would have to make every field optional, and then nothing
 * either side could rely on it.
 */
export interface AnnouncementFeedItem {
  announcementId: number;
  title: string;
  body: string;
  category: string;
  publishDate: string;
  pinned: boolean;
  audience: string;
  authorName: string | null;
}

/* ── Scorecard ──────────────────────────────────────────────────────────── */

export interface ScorecardMeasure {
  adminKpiId: number;
  kpiCode: string;
  name: string;
  category: string;
  description: string | null;
  ownerRoleName: string | null;
  unit: string;
  target: number;
  floorValue: number | null;
  direction: 'higher' | 'lower';
  frequency: string;
  dataSource: string | null;
  weight: number;
  actual: number | null;
  previous: number | null;
  commentary: string | null;
  status: KpiStatus;
  delta: number | null;
  /** Whether it moved the RIGHT way, which is not the sign of the delta — on a
   *  lower-is-better measure, down is better. Null when either period is
   *  missing, so "no comparison" never renders as "no improvement". */
  improving: boolean | null;
  attainmentPercent: number | null;
}

export interface ScorecardData {
  period: string;
  previousPeriod: string;
  summary: {
    total: number;
    measured: number;
    onTarget: number;
    atRisk: number;
    offTarget: number;
    noData: number;
    weightedHealth: number | null;
  };
  categories: {
    category: string;
    count: number;
    onTarget: number;
    atRisk: number;
    offTarget: number;
    noData: number;
    weightedHealth: number | null;
  }[];
  trend: {
    period: string;
    measured: number;
    onTarget: number;
    atRisk: number;
    offTarget: number;
    weightedHealth: number | null;
  }[];
  measures: ScorecardMeasure[];
}

export interface KpiHistoryData {
  definition: Omit<ScorecardMeasure,
    'actual' | 'previous' | 'commentary' | 'status' | 'delta' | 'improving' | 'attainmentPercent'>;
  history: { period: string; actual: number; commentary: string | null; status: KpiStatus }[];
}

/* ── Register rows ──────────────────────────────────────────────────────────
   PascalCase here, unlike the computed payloads above, because these ARE the
   columns — see the note at the top of this file. Every one carries the
   server-computed date arithmetic (`DaysTo…`) alongside the raw date, so the
   browser never decides what "overdue" means. */

export interface VendorRow {
  AdminVendorId: number;
  Name: string;
  Category: string;
  Tier: string;
  AccountManagerName: string | null;
  ContactEmail: string | null;
  ContactPhone: string | null;
  PortalUrl: string | null;
  PaymentTerms: string | null;
  AnnualSpend: number;
  AvgMarginPercent: number | null;
  RebateEligible: boolean;
  OnTimeShipPercent: number | null;
  RelationshipScore: number | null;
  LastReviewDate: string | null;
  NextReviewDate: string | null;
  InternalOwnerEmployeeId: number | null;
  InternalOwnerName: string | null;
  Status: string;
  Notes: string;
  DaysToReview: number | null;
  PurchaseOrderCount: number;
}

export interface CallMetricRow {
  CallMetricId: number;
  MetricDate: string;
  Queue: string;
  CallsOffered: number;
  CallsAnswered: number;
  CallsAbandoned: number;
  Voicemails: number;
  AfterHoursCalls: number;
  AvgSpeedAnswerSec: number;
  AvgHandleTimeSec: number;
  LongestWaitSec: number;
  ServiceLevelPercent: number;
  FirstCallResPercent: number;
  CsatScore: number | null;
}

export interface PurchaseOrderRow {
  PurchaseOrderId: number;
  PoNumber: string;
  AdminVendorId: number | null;
  VendorName: string | null;
  /** The catalogue line this order is for, where there is one — migration 102. */
  ProductId: number | null;
  ProductName: string | null;
  ClientId: number | null;
  ClientName: string | null;
  Description: string;
  Category: string;
  QuoteRequestedDate: string | null;
  QuoteSentDate: string | null;
  OrderDate: string;
  PromisedDate: string | null;
  ReceivedDate: string | null;
  QtyOrdered: number;
  QtyReceived: number;
  CostTotal: number;
  SellTotal: number;
  Status: string;
  ReceivingException: string | null;
  StockedToDepot: boolean;
  BuyerEmployeeId: number | null;
  BuyerName: string | null;
  Notes: string;
  /** Only while still open — a received order is history however late it was. */
  DaysOverdue: number | null;
  /** The signed slip on a completed receipt: negative is early. */
  ReceiptSlipDays: number | null;
  QuoteTurnaroundDays: number | null;
}

export interface ContractRow {
  ContractId: number;
  ContractName: string;
  PartyType: string;
  ClientId: number | null;
  ClientName: string | null;
  AdminVendorId: number | null;
  VendorName: string | null;
  CounterpartyName: string;
  ContractType: string;
  StartDate: string;
  EndDate: string;
  TermMonths: number | null;
  AutoRenew: boolean;
  NoticeDays: number;
  MonthlyValue: number;
  AnnualValue: number;
  Seats: number | null;
  OwnerEmployeeId: number | null;
  OwnerName: string | null;
  PriceIncreasePercent: number | null;
  LastReviewed: string | null;
  Status: string;
  DocumentLink: string | null;
  Notes: string;
  DaysToEnd: number;
  NoticeDeadline: string;
  DaysToNotice: number;
  NoticeWindowOpen: boolean;
}

export interface AssetRow {
  DepotAssetId: number;
  AssetTag: string;
  Category: string;
  /** The catalogue line, where the thing on the shelf is one we sell — 102. */
  ProductId: number | null;
  ProductName: string | null;
  Manufacturer: string | null;
  Model: string | null;
  SerialNumber: string | null;
  Condition: string;
  Status: string;
  DepotLocation: string | null;
  Bin: string | null;
  AssignedClientId: number | null;
  AssignedClientName: string | null;
  AssignedUserName: string | null;
  PurchaseOrderId: number | null;
  PoNumber: string | null;
  PurchaseDate: string | null;
  WarrantyExpiry: string | null;
  UnitCost: number;
  Quantity: number;
  LastAudited: string | null;
  Notes: string;
  /** Unit cost times quantity — one row can be forty patch leads. */
  LineValue: number;
  DaysToWarrantyExpiry: number | null;
  DaysSinceAudit: number | null;
}

export interface SopRow {
  SopRegisterId: number;
  SopCode: string;
  Title: string;
  Category: string;
  OwnerEmployeeId: number | null;
  OwnerName: string | null;
  Criticality: string;
  ReviewFrequencyMonths: number;
  LastReviewed: string | null;
  NextReview: string | null;
  Version: string;
  Status: string;
  DocumentLink: string | null;
  LinkedKpiId: number | null;
  LinkedKpiName: string | null;
  LinkedKpiCode: string | null;
  Notes: string;
  DaysToReview: number | null;
}

export interface RegistrationRow {
  RegistrationId: number;
  Name: string;
  ItemType: string;
  Issuer: string | null;
  Jurisdiction: string | null;
  Identifier: string | null;
  CoverageAmount: number | null;
  PremiumAnnual: number | null;
  IssueDate: string | null;
  ExpiryDate: string;
  RenewalLeadDays: number;
  OwnerEmployeeId: number | null;
  OwnerName: string | null;
  Status: string;
  DocumentLink: string | null;
  Notes: string;
  DaysToExpiry: number;
  ActionRequired: boolean;
}

export interface KpiRow {
  AdminKpiId: number;
  KpiCode: string;
  Name: string;
  Category: string;
  Description: string | null;
  OwnerRoleId: number | null;
  OwnerRoleName: string | null;
  Unit: string;
  Target: number;
  FloorValue: number | null;
  Direction: string;
  Frequency: string;
  DataSource: string | null;
  Weight: number;
  IsActive: boolean;
  ResultCount: number;
}

export interface KpiResultRow {
  AdminKpiResultId: number;
  AdminKpiId: number;
  KpiCode: string;
  KpiName: string;
  Unit: string;
  Target: number;
  FloorValue: number | null;
  Direction: string;
  Period: string;
  Actual: number;
  Commentary: string | null;
}

export interface AnnouncementRow {
  AnnouncementId: number;
  Title: string;
  Body: string;
  Category: string;
  AuthorEmployeeId: number | null;
  AuthorName: string | null;
  PublishDate: string;
  Pinned: boolean;
  Audience: string;
}

/* ── Pickers ────────────────────────────────────────────────────────────── */

export interface VendorOption {
  AdminVendorId: number;
  Name: string;
  Tier: string;
  Status: string;
}

export interface KpiOption {
  AdminKpiId: number;
  KpiCode: string;
  Name: string;
  Category: string;
  Unit: string;
  Target: number;
  FloorValue: number | null;
  Direction: string;
}

/** A price-book line, for saying what an order or an asset is for. */
export interface ProductOption {
  ProductId: number;
  Name: string;
  Category: string | null;
  Vendor: string | null;
}

export interface PurchaseOrderOption {
  PurchaseOrderId: number;
  PoNumber: string;
  Description: string;
  OrderDate: string;
}

/**
 * A company from the shared directory.
 *
 * There is no `RoleOption` beside this: a measure's accountable role uses the
 * directory's own `Role` from lib/types.ts, read from `GET /api/roles`.
 */
export interface ClientOption {
  ClientId: number;
  Name: string;
  Status: string;
  IsActive: boolean;
}

/* ── Status → band ─────────────────────────────────────────────────────────
   One mapping per vocabulary, so the same state is the same colour on every tab
   it appears on. Kept here rather than in each page for exactly that reason.

   `no_data` is neutral, not bad: nobody entered it, which is a different
   statement from missing the target. */

export const KPI_BANDS: Record<KpiStatus, Band> = {
  on_target: 'good',
  at_risk: 'warning',
  off_target: 'critical',
  no_data: 'neutral',
};

export const SEVERITY_BANDS: Record<Severity, Band> = {
  critical: 'critical',
  serious: 'serious',
  warning: 'warning',
};

export function contractBand(status: string): Band {
  switch (status) {
    case 'active':
      return 'good';
    case 'in_renewal':
      return 'warning';
    case 'notice_given':
      return 'serious';
    case 'expired':
      return 'critical';
    default:
      /* draft */
      return 'neutral';
  }
}

export function poBand(status: string): Band {
  switch (status) {
    case 'received':
      return 'good';
    case 'ordered':
    case 'partially_received':
      return 'warning';
    case 'quoting':
    case 'awaiting_approval':
      return 'neutral';
    default:
      /* cancelled */
      return 'neutral';
  }
}

export function assetBand(status: string): Band {
  switch (status) {
    case 'in_stock':
    case 'deployed':
      return 'good';
    case 'reserved':
    case 'staged':
      return 'neutral';
    case 'rma':
      return 'serious';
    case 'awaiting_disposal':
      return 'warning';
    default:
      /* retired */
      return 'neutral';
  }
}

export function sopBand(status: string): Band {
  switch (status) {
    case 'active':
      return 'good';
    case 'under_review':
      return 'warning';
    case 'draft':
      return 'neutral';
    default:
      /* retired */
      return 'neutral';
  }
}

export function registrationBand(status: string): Band {
  switch (status) {
    case 'current':
      return 'good';
    case 'renewal_in_progress':
      return 'warning';
    case 'lapsed':
      return 'critical';
    default:
      /* not_required */
      return 'neutral';
  }
}

export function criticalityBand(criticality: string): Band {
  switch (criticality) {
    case 'critical':
      return 'critical';
    case 'high':
      return 'serious';
    case 'medium':
      return 'warning';
    default:
      return 'neutral';
  }
}

export function tierBand(tier: string): Band {
  switch (tier) {
    case 'strategic':
      return 'good';
    case 'preferred':
      return 'neutral';
    case 'watchlist':
      return 'serious';
    default:
      /* transactional */
      return 'neutral';
  }
}
